import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { userApiClient } from '../../userApiClient';
import { PassInstanceStatus, PassTargetDetail, PassWalletSummary } from '../../models/Pass';
import PassTargetTypeChip from '../../components/passes/PassTargetTypeChip';
import PassInstanceStatusChip from '../../components/passes/PassInstanceStatusChip';
import {
  formatPassMoney,
  formatPassValidUntil,
  passDaysRemaining,
  passQuantityLabel,
  passUsedQuantity,
} from '../../utils/passFormatting';
import { fetchPassTargetsMap, passTargetSummaryFromIds } from '../../utils/passTargets';
import SEO from '../../components/SEO';
import { passDetailPageSx } from '../../components/passes/passDetailLayout';

type WalletTab = 'active' | 'history';

const HISTORY_STATUSES: PassInstanceStatus[] = ['EXPIRED', 'EXHAUSTED', 'CANCELLED'];
const USER_PASSES_MAX_WIDTH = 1200;

export default function UserPassesPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [passes, setPasses] = useState<PassWalletSummary[]>([]);
  const [targetById, setTargetById] = useState<Map<string, PassTargetDetail>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<WalletTab>('active');

  const fetchPasses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userApiClient.listWalletPasses();
      setPasses(data);
      const targets = await fetchPassTargetsMap(data);
      setTargetById(targets);
    } catch (err) {
      console.error('Error fetching wallet passes:', err);
      setError(t('userPasses.fetchError', 'Failed to load your passes'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchPasses();
  }, [fetchPasses]);

  const activePasses = useMemo(
    () => passes.filter((pass) => pass.status === 'ACTIVE' || pass.status === 'PENDING_PAYMENT'),
    [passes],
  );
  const historyPasses = useMemo(
    () => passes.filter((pass) => HISTORY_STATUSES.includes(pass.status)),
    [passes],
  );

  const visiblePasses = activeTab === 'active' ? activePasses : historyPasses;

  const renderEmpty = () => (
    <Card elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: 'action.hover' }}>
      <Typography variant="h6" gutterBottom>
        {activeTab === 'active'
          ? t('userPasses.noActive', 'No active passes')
          : t('userPasses.noHistory', 'No past passes yet')}
      </Typography>
    </Card>
  );

  const renderPassCard = (pass: PassWalletSummary) => {
    const pendingActivation = pass.status === 'PENDING_PAYMENT';
    const validUntilLabel = formatPassValidUntil(pass.validUntil, i18n.language);
    const purchasedAtLabel = formatPassValidUntil(pass.purchasedAt, i18n.language);
    const daysLeft = passDaysRemaining(pass.validUntil);
    const usedQuantity = passUsedQuantity(pass.remainingQuantity, pass.totalQuantity);
    const usedRatio =
      pass.totalQuantity > 0 ? Math.min(1, Math.max(0, usedQuantity / pass.totalQuantity)) : 0;
    const priceLabel = formatPassMoney(pass.purchasePrice, pass.purchaseCurrencyCode, i18n.language);
    const targetSummary = passTargetSummaryFromIds(pass.targetIds, targetById);

    const validityHint = (() => {
      if (pendingActivation) {
        return t(
          'userPasses.pendingActivationHint',
          'Awaiting host activation. Pay on site when you visit.',
        );
      }
      if (!validUntilLabel && !pass.validUntil) {
        const days = pass.validityDurationDays;
        if (days != null && days > 0) {
          return t(
            'userPasses.startsOnFirstUseWithDays',
            'Valid for {{count}} days from first use',
            { count: days }
          );
        }
        return t('userPasses.startsOnFirstUse', 'Starts on first use');
      }
      if (daysLeft == null || !validUntilLabel) return null;
      if (daysLeft < 0) {
        return t('userPasses.expiredOn', 'Expired {{date}}', { date: validUntilLabel });
      }
      if (daysLeft === 0) {
        return t('userPasses.expiresToday', 'Expires today');
      }
      return t('userPasses.daysLeft', '{{count}} days left', { count: daysLeft });
    })();

    const cardBody = (
      <CardContent sx={{ width: '100%' }}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
              <ConfirmationNumberIcon
                color={pendingActivation ? 'disabled' : 'primary'}
                fontSize="small"
                sx={{ flexShrink: 0, mt: 0.35 }}
              />
              <Stack spacing={0.35} sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                  {pass.title}
                </Typography>
                {pass.organizationName?.trim() ? (
                  <Typography variant="caption" color="text.secondary">
                    {t('userPasses.purchasedFromOrganization', 'Purchased from {{name}}', {
                      name: pass.organizationName.trim(),
                    })}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
              <PassTargetTypeChip targetType={pass.targetType} />
              <PassInstanceStatusChip status={pass.status} />
            </Stack>
          </Stack>

          {targetSummary ? (
            <Typography variant="body2" color="text.secondary">
              {targetSummary}
            </Typography>
          ) : null}

          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 0.75 }}>
              {passQuantityLabel(usedQuantity, pass.totalQuantity, pass.unitType, t)}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={pendingActivation ? 0 : usedRatio * 100}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: 'action.hover',
                '& .MuiLinearProgress-bar': { borderRadius: 3 },
              }}
            />
          </Box>

          <Stack spacing={0.35}>
            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="baseline">
              <Typography variant="caption" color="text.secondary">
                {purchasedAtLabel
                  ? t('userPasses.purchasedOn', 'Purchased {{date}}', { date: purchasedAtLabel })
                  : t('userPasses.details.purchasedAt', 'Purchased')}
              </Typography>
              <Stack direction="row" spacing={0.4} alignItems="center" sx={{ flexShrink: 0 }}>
                <PaymentsRoundedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" fontWeight={700}>
                  {priceLabel}
                </Typography>
              </Stack>
            </Stack>
            {(validUntilLabel || validityHint) && (
              <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="baseline">
                {validUntilLabel && !pendingActivation ? (
                  <Typography variant="caption" color="text.secondary">
                    {t('userPasses.validUntil', 'Valid until {{date}}', { date: validUntilLabel })}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {validityHint}
                  </Typography>
                )}
                {validUntilLabel && validityHint && !pendingActivation && (
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color={daysLeft != null && daysLeft <= 7 ? 'warning.main' : 'text.secondary'}
                  >
                    {validityHint}
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        </Stack>
      </CardContent>
    );

    return (
      <Grid item xs={12} sm={6} md={4} key={pass.id}>
        <Card
          variant="outlined"
          sx={{
            height: '100%',
            borderRadius: 2.5,
            display: 'flex',
            flexDirection: 'column',
            borderColor: pendingActivation ? 'info.light' : 'divider',
            bgcolor: pendingActivation ? 'action.hover' : undefined,
          }}
        >
          {pendingActivation ? (
            cardBody
          ) : (
            <CardActionArea
              onClick={() => navigate(`/passes/${pass.id}`)}
              sx={{ flex: 1, display: 'flex', alignItems: 'stretch' }}
            >
              {cardBody}
            </CardActionArea>
          )}
        </Card>
      </Grid>
    );
  };

  return (
    <>
      <SEO title={t('userPasses.title', 'My Passes')} noindex />
      <Box sx={{ ...passDetailPageSx, maxWidth: USER_PASSES_MAX_WIDTH }}>
        <Card elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            {t('userPasses.title', 'My Passes')}
          </Typography>
          <Divider sx={{ mb: { xs: 2, sm: 3 } }} />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)}>
              <Tab
                value="active"
                label={`${t('userPasses.tabActive', 'Active')} (${activePasses.length})`}
              />
              <Tab
                value="history"
                label={`${t('userPasses.tabHistory', 'History')} (${historyPasses.length})`}
              />
            </Tabs>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : visiblePasses.length === 0 ? (
            renderEmpty()
          ) : (
            <Grid container spacing={2}>
              {visiblePasses.map(renderPassCard)}
            </Grid>
          )}
        </Card>
      </Box>
    </>
  );
}
